const HEADER_SEPARATOR = /\r?\n\r?\n/;

const splitHeaderAndBody = (source = '') => {
  const match = HEADER_SEPARATOR.exec(source);
  if (!match) return {headerText: source, bodyText: ''};
  return {
    headerText: source.slice(0, match.index),
    bodyText: source.slice(match.index + match[0].length),
  };
};

const parseHeaders = (headerText = '') => {
  const headers = {};
  const unfolded = String(headerText).replace(/\r?\n[ \t]+/g, ' ');
  unfolded.split(/\r?\n/).forEach((line) => {
    const separatorIndex = line.indexOf(':');
    if (separatorIndex <= 0) return;
    const name = line.slice(0, separatorIndex).trim().toLowerCase();
    const value = line.slice(separatorIndex + 1).trim();
    headers[name] = headers[name] ? `${headers[name]}, ${value}` : value;
  });
  return headers;
};

const parseHeaderParameters = (headerValue = '') => {
  const [type = '', ...params] = String(headerValue).split(';');
  const parsed = {type: type.trim().toLowerCase()};
  params.forEach((item) => {
    const separatorIndex = item.indexOf('=');
    if (separatorIndex < 0) return;
    const key = item.slice(0, separatorIndex).trim().toLowerCase();
    const value = item.slice(separatorIndex + 1).trim().replace(/^"|"$/g, '');
    parsed[key] = value;
  });
  return parsed;
};

const decodeText = (bytes, charset = 'utf-8') => {
  const normalizedCharset = String(charset || 'utf-8').trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
  const preferredCharset = ['gb2312', 'gbk', 'gb18030'].includes(normalizedCharset) ? 'gbk' : normalizedCharset;
  try {
    return new TextDecoder(preferredCharset || 'utf-8').decode(bytes);
  } catch (error) {
    return new TextDecoder('utf-8').decode(bytes);
  }
};

const decodeBase64 = (value, charset) => {
  try {
    const binary = atob(String(value).replace(/\s/g, ''));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return decodeText(bytes, charset);
  } catch (error) {
    return String(value || '');
  }
};

const decodeQuotedPrintable = (value, charset) => {
  const source = String(value || '').replace(/=\r?\n/g, '');
  const bytes = [];
  for (let index = 0; index < source.length; index += 1) {
    if (source[index] === '=' && /^[0-9a-f]{2}$/i.test(source.slice(index + 1, index + 3))) {
      bytes.push(parseInt(source.slice(index + 1, index + 3), 16));
      index += 2;
    } else {
      bytes.push(source.charCodeAt(index) & 0xff);
    }
  }
  return decodeText(new Uint8Array(bytes), charset);
};

const decodeEncodedWords = (value = '') => String(value).replace(
  /=\?([^?]+)\?([bq])\?([^?]*)\?=/gi,
  (match, charset, encoding, content) => (
    String(encoding).toLowerCase() === 'b'
      ? decodeBase64(content, charset)
      : decodeQuotedPrintable(String(content).replace(/_/g, ' '), charset)
  ),
);

const decodeBody = (body, transferEncoding, charset) => {
  const encoding = String(transferEncoding || '').toLowerCase();
  if (encoding.includes('base64')) return decodeBase64(body, charset);
  if (encoding.includes('quoted-printable')) return decodeQuotedPrintable(body, charset);
  return String(body || '');
};

const getFilename = (headers, contentType, disposition) => {
  const rawName = disposition['filename*'] || disposition.filename || contentType['name*'] || contentType.name || '';
  const encodedName = rawName.replace(/^([^']*)'[^']*'/, '');
  try {
    return decodeEncodedWords(decodeURIComponent(encodedName));
  } catch (error) {
    return decodeEncodedWords(encodedName);
  }
};

const splitMultipart = (body, boundary) => {
  if (!boundary) return [];
  const delimiter = `--${boundary}`;
  return String(body)
    .split(delimiter)
    .slice(1)
    .map((part) => part.replace(/^\r?\n/, '').replace(/\r?\n$/, ''))
    .filter((part) => part && !part.startsWith('--'));
};

const mergeParsedParts = (parts) => parts.reduce((result, part) => ({
  html: result.html || part.html,
  text: result.text || part.text,
  attachments: [...result.attachments, ...part.attachments],
  inlineResources: {...result.inlineResources, ...part.inlineResources},
}), {html: '', text: '', attachments: [], inlineResources: {}});

const parseMimeEntity = (source, depth = 0) => {
  if (depth > 12) return {html: '', text: '', attachments: [], inlineResources: {}};
  const {headerText, bodyText} = splitHeaderAndBody(source);
  const headers = parseHeaders(headerText);
  const contentType = parseHeaderParameters(headers['content-type'] || 'text/plain');
  const disposition = parseHeaderParameters(headers['content-disposition'] || '');

  if (contentType.type.startsWith('multipart/')) {
    return mergeParsedParts(splitMultipart(bodyText, contentType.boundary).map((part) => parseMimeEntity(part, depth + 1)));
  }

  const contentId = String(headers['content-id'] || '').replace(/^<|>$/g, '').toLowerCase();
  if (contentId && contentType.type.startsWith('image/') && String(headers['content-transfer-encoding'] || '').toLowerCase().includes('base64')) {
    const data = String(bodyText || '').replace(/\s/g, '');
    return {
      html: '',
      text: '',
      attachments: [],
      inlineResources: data ? {[contentId]: `data:${contentType.type};base64,${data}`} : {},
    };
  }

  const filename = getFilename(headers, contentType, disposition);
  const isAttachment = Boolean(filename) || disposition.type === 'attachment';
  if (isAttachment) {
    return {
      html: '',
      text: '',
      attachments: [{name: filename || 'attachment', contentType: contentType.type || 'application/octet-stream'}],
      inlineResources: {},
    };
  }

  const content = decodeBody(bodyText, headers['content-transfer-encoding'], contentType.charset);
  if (contentType.type === 'text/html') return {html: content, text: '', attachments: [], inlineResources: {}};
  if (contentType.type === 'text/plain' || !contentType.type) return {html: '', text: content, attachments: [], inlineResources: {}};
  return {html: '', text: '', attachments: [], inlineResources: {}};
};

export const parseEml = (source) => {
  const {headerText, bodyText} = splitHeaderAndBody(source);
  const headers = parseHeaders(headerText);
  const parsed = parseMimeEntity(source);

  return {
    headers: {
      from: decodeEncodedWords(headers.from || ''),
      to: decodeEncodedWords(headers.to || ''),
      cc: decodeEncodedWords(headers.cc || ''),
      subject: decodeEncodedWords(headers.subject || ''),
      date: headers.date || '',
    },
    html: parsed.html,
    text: parsed.text || (!parsed.html ? bodyText : ''),
    attachments: parsed.attachments,
    inlineResources: parsed.inlineResources,
  };
};
